/**
 * Shared HP bar (#681) — extracted from `OwnFrame`/`TargetFrame`/
 * `FriendlyTargetFrame`/`UnitFrameStack`, which each defined a byte-identical
 * (or near-identical, modulo `height`) local `HpBar` function. Mirrors the
 * existing `FrameCastBar`/`FrameResourceBar` shared-component pattern already
 * used on these same frames.
 *
 * `testId` is the FULL data-testid string (not a prefix): unlike
 * `FrameCastBar`'s `testIdPrefix` convention, the four existing HP-bar
 * testids (`own-hp-bar`, `target-hp-bar`, `friendly-target-hp-bar`,
 * `unit-frame-hp-bar`) don't follow a uniform `{prefix}-hp-bar` shape, so
 * each call site passes its exact pre-existing testid through unchanged —
 * zero test-visible behavior change.
 *
 * Prop shape is intentionally minimal + additive-friendly: #680 (shield/
 * absorb overlay) is expected to extend this with a `shieldAmount`-style
 * prop without renaming anything here.
 */

import type { ReactElement } from "react";
import { HP_BAR_FILL_COLOR, hpFraction } from "./hud-util.js";

export function HpBar({
  hp,
  maxHp,
  height = 10,
  testId,
}: {
  readonly hp: number;
  readonly maxHp: number;
  /** Bar height, px. Defaults to the 10px convention; `UnitFrameStack` passes 8. */
  readonly height?: number;
  readonly testId: string;
}): ReactElement {
  const f = hpFraction(hp, maxHp);
  return (
    <div
      data-testid={testId}
      data-hp={String(hp)}
      style={{
        width: "100%",
        height,
        background: "#111",
        border: "1px solid #333",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${f * 100}%`,
          height: "100%",
          // #344: always green — the fraction is already conveyed by width.
          background: HP_BAR_FILL_COLOR,
        }}
      />
    </div>
  );
}
